import { useOutsideClick } from "@/hooks/useOutsideClick";
import React, { useState, useRef, useEffect } from "react";
import { motion } from "motion/react";
import { getCookie } from "@/lib/cookies";
import { API_URL } from "@/env";
import { useWorkspaceStore } from "@/hooks/useWorkspace";
import useRecordingLinksStore from "@/hooks/useRecordingLinks";
import CopyWhite from "@/assets/icons/CopyWhite.svg";
import Link from "@/assets/icons/Link.svg";
import { showCustomToast } from "../CustomToast";

const RecordingLinkModal = ({
  setIsOpen,
}: {
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const token = getCookie("token");
  const [title, setTitle] = useState("");
  const [redirectLink, setRedirectLink] = useState("");
  const [showRedirectInput, setShowRedirectInput] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [error, setError] = useState<string>("");
  const modalRef = useRef(null);

  const { currentWorkspace } = useWorkspaceStore();
  const { addRecordingLink, recordingLinks } = useRecordingLinksStore();

  useOutsideClick(modalRef, () => {
    setIsOpen(false);
  });

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [setIsOpen]);

  const handleGenerate = async () => {
    if (title.trim()) {
      const titleExists = recordingLinks.some(
        (link) =>
          link.name?.toLowerCase().trim() === title.toLowerCase().trim() ||
          link.title?.toLowerCase().trim() === title.toLowerCase().trim()
      );

      if (titleExists) {
        setError("A recording link with this name already exists");
        return;
      }
    }

    try {
      setIsLoading(true);

      console.log({ redirectLink });

      const response = await fetch(`${API_URL}/recording-links`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          workspaceId: currentWorkspace?._id,
          title: title || undefined,
          redirectLink: redirectLink || undefined,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to generate recording link");
      }

      const result = await response.json();

      // Add the new recording link to the store
      if (result._id) {
        addRecordingLink(result);
      }

      const linkUrl = `${window.location.origin}/record/${result.token}`;
      setGeneratedLink(linkUrl);

      // Automatically copy to clipboard
      navigator.clipboard.writeText(linkUrl);
      showCustomToast("Link copied to clipboard", "check");
    } catch (error) {
      console.error("Error generating recording link:", error);
      showCustomToast("Failed to generate link", "exclamation");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (generatedLink) {
      navigator.clipboard.writeText(generatedLink);
      showCustomToast("Link copied to clipboard", "check");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="onest-md bg-gray-50/[0.72] backdrop-blur-md z-[100000] fixed w-screen h-screen flex items-center justify-center"
    >
      <div
        className="p-6 flex flex-col rounded-[24px] w-[480px] bg-gray-200"
        ref={modalRef}
      >
        <div>
          <p className="text-[20px] leading-[28px] font-medium text-white">
            Generate recording link
          </p>
          <p className="mt-2 text-[14px] leading-[20px] text-white/[0.32] font-medium">
            Create a shareable link for non-users to submit screen recordings
          </p>

          {!generatedLink && (
            <div className="mt-6 flex flex-col gap-6">
              <div>
                <p className="text-[14px] leading-[20px] text-white/[0.32] font-medium">
                  Title (optional)
                </p>
                <input
                  type="text"
                  autoFocus
                  placeholder="e.g., Bug report for checkout flow"
                  className={`${
                    error ? "border-red-700" : "border-white/[0.08]"
                  } mt-2 rounded-[12px] h-[36px] w-full text-[14px] leading-[20px] px-4 outline-none ring-0 placeholder:text-white/[0.32] text-white bg-white/[0.04] border-[1px] py-[8px]`}
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    setError(""); // Clear error when user types
                  }}
                  disabled={isLoading}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !isLoading) {
                      handleGenerate();
                    }
                  }}
                />
                {error && (
                  <p className="mt-2 text-[12px] leading-[16px] text-red-700 font-medium">
                    {error}
                  </p>
                )}
              </div>

              <div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowRedirectInput(!showRedirectInput);
                      if (showRedirectInput) {
                        setRedirectLink("");
                      }
                    }}
                    className={`relative inline-flex items-center h-[20px] w-[32px] shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-0 outline-none border-none p-[2px] ${
                      showRedirectInput ? "bg-white" : "bg-white/[0.16]"
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-[16px] w-[16px] transform rounded-full shadow-lg ring-0 transition duration-200 ease-in-out ${
                        showRedirectInput
                          ? "translate-x-3 bg-black"
                          : "translate-x-0 bg-white/[0.16]"
                      }`}
                    />
                  </button>
                  <p className="text-[14px] leading-[20px] text-white/[0.32] font-medium">
                    Add redirect link
                  </p>
                </div>

                {showRedirectInput && (
                  <div className="mt-6">
                    <input
                      type="url"
                      placeholder="e.g., https://example.com"
                      className="rounded-[12px] h-[36px] w-full text-[14px] leading-[20px] px-4 outline-none ring-0 placeholder:text-white/[0.32] text-white bg-white/[0.04] border-[1px] border-white/[0.08] py-[8px]"
                      value={redirectLink}
                      onChange={(e) => setRedirectLink(e.target.value)}
                      disabled={isLoading}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !isLoading) {
                          handleGenerate();
                        }
                      }}
                    />
                    <p className="mt-6 text-[14px] leading-[20px] text-white/[0.32] font-medium">
                      User will be redirected to this URL when they start
                      recording
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {!generatedLink ? (
            <button
              onClick={handleGenerate}
              disabled={isLoading}
              className="mt-6 w-full h-[36px] px-4 disabled:text-white/[0.32] disabled:bg-white/[0.04] transition-all text-[14px] leading-[20px] font-medium bg-white hover:bg-white/[0.8] text-gray-50 rounded-[12px] flex items-center justify-center gap-2"
            >
              {isLoading ? (
                "Generating..."
              ) : (
                <>
                  <img src={Link} className="w-4 h-4" />
                  Generate link
                </>
              )}
            </button>
          ) : (
            <div className="mt-6">
              <p className="text-[14px] leading-[20px] text-white/[0.32] font-medium">
                Share this link
              </p>
              <div
                className="mt-2 relative cursor-pointer"
                onClick={handleCopyLink}
              >
                <div className="rounded-[12px] h-[36px] w-full text-[14px] truncate leading-[20px] px-4 pr-12 outline-none ring-0 text-white bg-white/[0.04] border-[1px] border-white/[0.08] py-2">
                  {generatedLink}
                </div>
                <button className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-[8px] transition-all">
                  <img src={CopyWhite} className="w-5 h-5" alt="Copy" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default RecordingLinkModal;
